import { useQuery } from "@tanstack/react-query";
import { ConnectionStatus } from "@workspace/mobile";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { selectedDateBookingList } from "../../APIs/Booking/GetBookings";
import { updateUnsavedBookings } from "../../APIs/Booking/UpdateUnsavedBookings";
import { errorHandler } from "../../APIs/errorHandler";
import { PageContainer } from "../../components/Layout/PageContainer/PageContainer";
import { DiaryHeader } from "../../components/Pages/Diary/Header/DiaryHeader";
import DiaryMain from "../../components/Pages/Diary/Main/DiaryMain";
import { DiarySidebar } from "../../components/Pages/Diary/Sidebar/DiarySidebar/DiarySidebar";
import { gridSizeOptions } from "../../components/Pages/Diary/ViewGridConfig/ViewGridConfigController";
import { useDeviceContext } from "../../context/DeviceContext";
import { useDiaryContext } from "../../hooks/useDiaryContext";
import useUpdateStateFromURL from "../../hooks/useUpdateStateFromURL";
import { useVisibilityChange } from "../../hooks/useVisibilityChange";
import { customMessageEvent, useWebSocket } from "../../hooks/useWebSocket";
import { WebsocketBookingUpdated } from "../../types/websocketBookingUpdated";
import {
  getOpenCloseTimeInMinutes,
  mapWebsocketBookingToState,
  updateStateBooking
} from "../../utils/booking";
import { getBookingCacheStorage } from "../../utils/localStorage/apiCacheStorage";
import { getFloorPlanConfigStorage } from "../../utils/localStorage/floorPlanConfigStorage";
import { getGridConfigStorage } from "../../utils/localStorage/gridConfigStorage";

export const DiaryPage = (): React.JSX.Element => {
  useUpdateStateFromURL();
  const diaryContext = useDiaryContext();
  const { connectionStatus } = useDeviceContext();

  // Log previous connection status
  const connectionStatusChanged = useRef<boolean>(false);
  const connectionStatusString = useRef<ConnectionStatus>(connectionStatus);

  const [channel, setChannel] = useState<string>("");

  const diaryContextDate = diaryContext.date;
  const diaryContextDateFormatted = diaryContextDate
    .toISOString()
    .split("T")[0];
  const diaryContextVenueConfigId = diaryContext.venueConfig?._id;

  useEffect(() => {
    connectionStatusString.current = connectionStatus;
  }, []);

  const checkUnsavedBookings = async () => {
    if (
      connectionStatusString.current === "internet" &&
      connectionStatusChanged.current
    ) {
      await updateUnsavedBookings({ diaryContext });
    }
  };

  useEffect(() => {
    if (connectionStatusString.current !== connectionStatus) {
      connectionStatusString.current = connectionStatus;
      connectionStatusChanged.current = true;
    }

    checkUnsavedBookings();
  }, [connectionStatus]);

  const onMessage = useCallback(
    (event: customMessageEvent<WebsocketBookingUpdated>) => {
      if (event.channel !== channel) {
        return;
      }
      try {
        const booking = mapWebsocketBookingToState(event.message);
        updateStateBooking(booking, diaryContext);
      } catch (error) {
        errorHandler(error, "Error updating booking from websocket");
      }
    },
    [diaryContext, channel]
  );

  const updateChanel = useCallback(() => {
    if (diaryContext.venueConfig?._id) {
      setChannel(
        `updated_bookings_${diaryContextVenueConfigId}_${
          diaryContextDateFormatted
        }`
      );
    }
  }, [diaryContextVenueConfigId, diaryContextDateFormatted]);

  useEffect(() => {
    updateChanel();
    return () => {
      setChannel("");
    };
  }, [updateChanel]);

  useWebSocket({
    channel,
    onMessage
  });

  useVisibilityChange((visible: boolean) => {
    setChannel("");

    if (visible && diaryContextVenueConfigId === diaryContext.selectedVenue) {
      fetchBookingList();
      updateChanel();
    }
  });

  const { data: bookingsData, refetch: fetchBookingList } = useQuery({
    queryKey: [
      "getBookingsList",
      diaryContextDateFormatted,
      diaryContextVenueConfigId
    ],
    queryFn: async () => {
      if (!diaryContextVenueConfigId) {
        return [];
      }

      if (connectionStatusString.current !== "internet") {
        return await getBookingCacheStorage(
          diaryContextDate,
          diaryContextVenueConfigId
        );
      }

      return selectedDateBookingList(
        diaryContextDate,
        diaryContextVenueConfigId
      );
    },
    refetchInterval: 300000,
    enabled:
      !!diaryContextVenueConfigId &&
      diaryContextVenueConfigId === diaryContext.selectedVenue
  });

  useEffect(() => {
    if (bookingsData) {
      diaryContext.setDiaryContext({
        bookings: bookingsData,
        fetchBookingList
      });
    }
  }, [bookingsData]);

  const opening_times = diaryContext.venueConfig?.opening_times;
  useEffect(() => {
    (async () => {
      if (opening_times) {
        const gridConfigStorage = await getGridConfigStorage();
        const floorPlanConfigStorage = await getFloorPlanConfigStorage();
        const sizeItem =
          gridSizeOptions[
            gridConfigStorage?.size ?? diaryContext.gridConfig.size
          ];

        const gridConfig = {
          ...diaryContext.gridConfig,
          ...gridConfigStorage,
          ...sizeItem,
          ...getOpenCloseTimeInMinutes({
            date: diaryContext.date,
            openingTimes: diaryContext.venueConfig?.opening_times
          })
        };

        diaryContext.setDiaryContext({
          gridConfig,
          floorPlanConfig: floorPlanConfigStorage
        });
      }
    })();
  }, [opening_times]);

  return (
    <PageContainer
      header={{
        content: <DiaryHeader />
      }}
      sidebar={
        diaryContext.selectedView === "RunsheetTab"
          ? undefined
          : {
              content: <DiarySidebar />
            }
      }
      main={{
        content: <DiaryMain />
      }}
      isRunsheetBeta={diaryContext.selectedView === "RunsheetTab"}
    />
  );
};
